// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: recipient chips with a people lookup (Graph people + directory).
// Shared by the mail composer and the calendar event attendee field.

import styles from '@app/features/konvo_mail/components/MailComposeModal.module.css';
import KonvoMail, {type KonvoMailAddress, type KonvoMailContact} from '@app/features/konvo_mail/state/KonvoMail';
import {CircleNotchIcon, XIcon} from '@app/features/ui/icons/Icons';
import {usePortalHost} from '@app/features/ui/overlay/PortalHostContext';
import {autoUpdate, FloatingPortal, flip, offset, shift, size, useFloating} from '@floating-ui/react';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import type React from 'react';
import {useCallback, useEffect, useId, useRef, useState} from 'react';

const RECIPIENT_PLACEHOLDER_DESCRIPTOR = msg({
	message: 'Type a name or email address',
	comment: 'Mail recipient field placeholder.',
});
const REMOVE_DESCRIPTOR = msg({message: 'Remove', comment: 'Remove a recipient chip or attachment.'});
const INVALID_ADDRESS_DESCRIPTOR = msg({
	message: 'That is not a valid email address.',
	comment: 'Validation message in the mail recipient field.',
});
const SEARCHING_DESCRIPTOR = msg({message: 'Searching…', comment: 'Status shown while looking up mail recipients.'});
const EXTERNAL_DESCRIPTOR = msg({
	message: 'External',
	comment: "Tag on a mail recipient suggestion whose address is outside the sender's organisation.",
});
const NO_PEOPLE_FOUND_DESCRIPTOR = msg({
	message: 'No people found',
	comment: 'Empty state when no mail recipients match the current search.',
});

export const EMAIL_RE = /^[^\s@<>]+@[^\s@<>]+\.[^\s@<>]+$/;

interface RecipientFieldProps {
	label: string;
	value: Array<KonvoMailAddress>;
	onChange: (value: Array<KonvoMailAddress>) => void;
	autoFocus?: boolean;
	/**
	 * "inline" is the phone composer's Outlook-style row: label on the left, chips beside it.
	 * "row" is the desktop composer's version: a hairline row with avatar chips.
	 */
	variant?: 'stacked' | 'inline' | 'row';
	/** Control rendered at the end of an inline row (the Cc/Bcc reveal). */
	trailing?: React.ReactNode;
	/** The sender's own domain; suggestions from other domains get an "External" tag. */
	selfDomain?: string | null;
}

export function initialsOf(entry: KonvoMailAddress): string {
	const source = (entry.name ?? entry.address.split('@')[0] ?? '').trim();
	const words = source.split(/[\s._-]+/).filter(Boolean);
	const letters = words.length > 1 ? `${words[0]![0]}${words[words.length - 1]![0]}` : source.slice(0, 2);
	return letters.toUpperCase();
}

/** Same pastel for the same person every time: a hue from the address. */
export function hueOf(address: string): number {
	let hash = 0;
	for (const char of address.toLowerCase()) hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
	return hash % 360;
}

function domainOf(address: string): string {
	return address.slice(address.lastIndexOf('@') + 1).toLowerCase();
}

export const RecipientField: React.FC<RecipientFieldProps> = ({
	label,
	value,
	onChange,
	autoFocus,
	variant = 'stacked',
	trailing,
	selfDomain,
}) => {
	const {i18n} = useLingui();
	const id = useId();
	const listboxId = `${id}-suggestions`;
	const inputRef = useRef<HTMLInputElement | null>(null);
	const [text, setText] = useState('');
	const [suggestions, setSuggestions] = useState<Array<KonvoMailContact>>([]);
	const [open, setOpen] = useState(false);
	const [loading, setLoading] = useState(false);
	const [hasSearched, setHasSearched] = useState(false);
	const [active, setActive] = useState(0);
	const [error, setError] = useState<string | null>(null);
	const abortRef = useRef<AbortController | null>(null);
	const portalHost = usePortalHost();
	const menuOpen = open && (loading || hasSearched || suggestions.length > 0);
	const {refs, floatingStyles} = useFloating({
		open: menuOpen,
		placement: 'bottom-start',
		strategy: 'fixed',
		whileElementsMounted: autoUpdate,
		middleware: [
			offset(4),
			flip({padding: 12}),
			shift({padding: 12}),
			size({
				padding: 12,
				apply({availableHeight, elements, rects}) {
					Object.assign(elements.floating.style, {
						width: `${rects.reference.width}px`,
						maxHeight: `${Math.min(256, availableHeight)}px`,
					});
				},
			}),
		],
	});

	useEffect(() => {
		if (autoFocus) inputRef.current?.focus();
	}, [autoFocus]);

	useEffect(() => {
		if (!open) return;
		abortRef.current?.abort();
		const controller = new AbortController();
		abortRef.current = controller;
		setLoading(true);
		const timer = setTimeout(async () => {
			const results = await KonvoMail.searchContacts(text.trim(), controller.signal);
			if (controller.signal.aborted) return;
			const chosen = new Set(value.map((entry) => entry.address.toLowerCase()));
			setSuggestions(results.filter((entry) => !chosen.has(entry.address.toLowerCase())));
			setActive(0);
			setHasSearched(true);
			setLoading(false);
		}, 200);
		return () => {
			clearTimeout(timer);
			controller.abort();
		};
	}, [text, open, value]);

	const commit = useCallback(
		(entry: KonvoMailAddress) => {
			if (value.some((existing) => existing.address.toLowerCase() === entry.address.toLowerCase())) {
				setText('');
				return;
			}
			onChange([...value, entry]);
			setText('');
			setError(null);
		},
		[onChange, value],
	);

	const commitText = useCallback((): boolean => {
		const raw = text.trim().replace(/[,;]+$/, '');
		if (!raw) return true;
		const match = /^(.*?)\s*<([^>]+)>$/.exec(raw);
		const address = (match ? match[2] : raw)!.trim();
		if (!EMAIL_RE.test(address)) {
			setError(i18n._(INVALID_ADDRESS_DESCRIPTOR));
			return false;
		}
		commit({name: match?.[1]?.trim() || null, address});
		return true;
	}, [commit, i18n, text]);

	const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (event.key === 'ArrowDown' && suggestions.length > 0) {
			event.preventDefault();
			setActive((index) => (index + 1) % suggestions.length);
		} else if (event.key === 'ArrowUp' && suggestions.length > 0) {
			event.preventDefault();
			setActive((index) => (index - 1 + suggestions.length) % suggestions.length);
		} else if (event.key === 'Enter' || event.key === 'Tab' || event.key === ',' || event.key === ';') {
			if (event.key === 'Tab' && !text.trim()) return;
			event.preventDefault();
			const pick = suggestions[active];
			if (pick && menuOpen && !loading && (event.key === 'Enter' || text.trim()))
				commit({name: pick.name, address: pick.address});
			else commitText();
		} else if (event.key === 'Backspace' && !text && value.length > 0) {
			onChange(value.slice(0, -1));
		} else if (event.key === 'Escape' && menuOpen) {
			// Only the suggestions close; the composer around the field must not also react.
			event.stopPropagation();
			setOpen(false);
		}
	};

	const row = variant === 'row';
	const inline = variant === 'inline' || row;

	const chipsClassName = clsx(
		styles.chips,
		inline && styles.chipsInline,
		inline && value.length > 0 && styles.chipsInlineFilled,
		row && styles.chipsRow,
		error && styles.chipsError,
	);
	const chipsInner = (
		<>
			{value.map((entry) => (
				<span key={entry.address} className={clsx(styles.chip, row && styles.chipRow)} title={entry.address}>
					{row ? (
						<span
							className={styles.chipAvatar}
							style={{'--chip-hue': hueOf(entry.address)} as React.CSSProperties}
							aria-hidden="true"
						>
							{initialsOf(entry)}
						</span>
					) : null}
					<span className={styles.chipLabel}>{entry.name ?? entry.address}</span>
					<button
						type="button"
						className={styles.chipRemove}
						aria-label={i18n._(REMOVE_DESCRIPTOR)}
						onClick={(event) => {
							event.stopPropagation();
							onChange(value.filter((existing) => existing.address !== entry.address));
						}}
						data-flx="konvo-mail.compose.chip-remove"
					>
						<XIcon size={11} weight="bold" />
					</button>
				</span>
			))}
			<input
				ref={inputRef}
				id={id}
				className={styles.chipInput}
				value={text}
				placeholder={value.length === 0 && !row ? i18n._(RECIPIENT_PLACEHOLDER_DESCRIPTOR) : ''}
				autoComplete="off"
				autoCapitalize="off"
				spellCheck={false}
				role="combobox"
				aria-autocomplete="list"
				aria-expanded={menuOpen}
				aria-controls={listboxId}
				aria-activedescendant={!loading && suggestions[active] ? `${listboxId}-option-${active}` : undefined}
				onChange={(event) => {
					setText(event.target.value);
					setError(null);
					setOpen(true);
				}}
				onFocus={() => setOpen(true)}
				onBlur={() => {
					window.setTimeout(() => {
						setOpen(false);
						commitText();
					}, 120);
				}}
				onKeyDown={handleKeyDown}
				data-flx="konvo-mail.compose.recipient-input"
			/>
		</>
	);

	// The inline row is a <label> so tapping anywhere along it puts the caret in the field, even
	// once the chips have used up the line.
	const chips = inline ? (
		<label ref={refs.setReference} htmlFor={id} className={chipsClassName}>
			{chipsInner}
		</label>
	) : (
		<div ref={refs.setReference} className={chipsClassName}>
			{chipsInner}
		</div>
	);

	const errorNode = error ? <div className={styles.fieldError}>{error}</div> : null;

	// Keep suggestions inside an enclosing popout's dismissal and focus boundary.
	// Portaling to the app host makes selecting an attendee look like an outside click.
	const suggestionHost = refs.domReference.current?.closest<HTMLElement>('[data-popout-key]') ?? portalHost;
	const suggestionMenu = menuOpen ? (
		<FloatingPortal root={suggestionHost ?? undefined} data-flx="konvo-mail.compose.suggestions-portal">
			<div
				id={listboxId}
				ref={refs.setFloating}
				className={styles.suggestions}
				style={floatingStyles}
				role="listbox"
				aria-busy={loading}
				data-flx="konvo-mail.compose.suggestions"
			>
				{loading ? (
					<div className={styles.suggestionState} role="status">
						<CircleNotchIcon className={styles.suggestionSpinner} size={16} />
						{i18n._(SEARCHING_DESCRIPTOR)}
					</div>
				) : suggestions.length === 0 ? (
					<div className={styles.suggestionState} role="status">
						{i18n._(NO_PEOPLE_FOUND_DESCRIPTOR)}
					</div>
				) : (
					suggestions.map((entry, index) => (
						<button
							key={entry.address}
							id={`${listboxId}-option-${index}`}
							type="button"
							role="option"
							aria-selected={index === active}
							className={clsx(styles.suggestion, index === active && styles.suggestionActive)}
							onPointerDown={(event) => event.preventDefault()}
							onMouseEnter={() => setActive(index)}
							onClick={() => commit({name: entry.name, address: entry.address})}
							data-flx="konvo-mail.compose.suggestion"
						>
							{row ? (
								<span
									className={clsx(styles.chipAvatar, styles.suggestionAvatarRow)}
									style={{'--chip-hue': hueOf(entry.address)} as React.CSSProperties}
									aria-hidden="true"
								>
									{initialsOf(entry)}
								</span>
							) : (
								<span className={styles.suggestionAvatar}>
									{(entry.name ?? entry.address).slice(0, 1).toUpperCase()}
								</span>
							)}
							<span className={styles.suggestionText}>
								<span className={styles.suggestionName}>{entry.name ?? entry.address}</span>
								{entry.name ? <span className={styles.suggestionAddress}>{entry.address}</span> : null}
							</span>
							{selfDomain && domainOf(entry.address) !== selfDomain.toLowerCase() ? (
								<span className={styles.suggestionTag}>{i18n._(EXTERNAL_DESCRIPTOR)}</span>
							) : null}
						</button>
					))
				)}
			</div>
		</FloatingPortal>
	) : null;

	if (inline) {
		return (
			<div className={clsx(styles.inlineField, row && styles.rowField)} data-flx="konvo-mail.compose.recipient-field">
				<label htmlFor={id} className={clsx(styles.inlineLabel, row && styles.rowLabel)}>
					{label}
				</label>
				<div className={styles.inlineValue}>
					{chips}
					{errorNode}
				</div>
				{trailing}
				{suggestionMenu}
			</div>
		);
	}

	return (
		<div className={styles.field} data-flx="konvo-mail.compose.recipient-field">
			<label htmlFor={id} className={styles.fieldLabel}>
				{label}
			</label>
			{chips}
			{errorNode}
			{suggestionMenu}
		</div>
	);
};
